import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Artist } from './types';

interface RosterTableProps {
  artists: Artist[];
  onDelete: (id: string) => void;
}

const RosterTable: React.FC<RosterTableProps> = ({ artists, onDelete }) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  if (artists.length === 0) {
    return (
      <div className="py-12 text-center">
        <svg
          className="w-12 h-12 mx-auto text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No artists</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by adding your first artist.</p>
        <div className="mt-6">
          <Link
            to="/roster/add-artist"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
          >
            Add Artist
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Photo
              </th>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Artist Name
              </th>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Pitches Submitted
              </th>
              <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {artists.map((artist, idx) => (
              <tr key={artist.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="w-10 h-10 overflow-hidden bg-gray-200 rounded-full">
                    {artist.photo ? (
                      <img
                        src={artist.photo}
                        alt={artist.artistName}
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to={`/roster/artist-details/${artist.id}?tab=details`}
                    className="text-sm font-medium text-gray-900 hover:text-indigo-600 hover:underline cursor-pointer"
                  >
                    {artist.artistName}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{artist.pitchCount}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === artist.id ? null : artist.id)}
                      className="flex items-center justify-center w-6 h-6 font-bold text-black hover:text-gray-700 focus:outline-none"
                      type="button"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </button>
                    {openMenuId === artist.id && (
                      <div className={
                        `absolute right-0 z-10 w-48 bg-white border border-gray-200 rounded-md shadow-lg ` +
                        (idx === 0 ? 'mt-2' : 'mb-2 bottom-full')
                      }>
                        {/* <Link
                          to={`/roster/artist-details/${artist.id}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setOpenMenuId(null)}
                        >
                          View Details
                        </Link> */}
                        <Link
                          to={`/roster/artist-details/${artist.id}?tab=pitches`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setOpenMenuId(null)}
                        >
                          View Pitches
                        </Link>
                        <Link
                          to={`/roster/artist-details/${artist.id}?tab=assets`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setOpenMenuId(null)}
                        >
                          View Assets
                        </Link>
                        <button
                          onClick={() => {
                            onDelete(artist.id);
                            setOpenMenuId(null);
                          }}
                          className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100"
                        >
                          Delete Artist
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RosterTable;